import React, { useEffect, useState } from 'react';
import { Button, CircularProgress, Container, Grid, Paper, styled, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAccountContext } from '../../contexts/account';
import { useFlashMessageContext } from '../../contexts/flashMessage';
import { buildUrl } from '../../utils';
import InputField from '../forms/InputField';

const Background = styled('div')({
  position: 'absolute',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
});

type ResetPasswordFormProps = {
  token: string;
};

export default function ResetPasswordForm({ token }: ResetPasswordFormProps) {
  const { setRefreshToken } = useAccountContext();
  const { addFlashMessage, clearFlashMessage } = useFlashMessageContext();
  const navigate = useNavigate();
  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
    watch,
  } = useForm({
    defaultValues: {
      password: '',
      passwordConfirm: '',
    },
  });
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);
  const password = watch('password', '');
  useEffect(() => {
    if (formHasBeenSubmitted) {
      trigger('passwordConfirm');
    }
  }, [password, formHasBeenSubmitted]);

  return (
    <Background>
      <Container maxWidth="sm" sx={{ height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Paper sx={{ p: 2 }}>
          <form
            onSubmit={handleSubmit(
              ({ password }) => {
                clearFlashMessage();
                setFormHasBeenSubmitted(true);
                return fetch(buildUrl('/api/reset-password'), {
                  headers: {
                    'Content-Type': 'application/json',
                    Accept: 'application/json',
                  },
                  method: 'POST',
                  mode: 'cors',
                  body: JSON.stringify({
                    password,
                    token,
                  }),
                })
                  .then(response => {
                    if (response.status === 200) {
                      return response.json().then(responseData => {
                        setRefreshToken(responseData.refresh);
                        navigate('/');
                      });
                    }
                    if (response.status === 400) {
                      return response.json().then(responseData => addFlashMessage('warning', responseData.message));
                    }
                    addFlashMessage('error', 'An unknown error occurred');
                  })
                  .catch(() => addFlashMessage('error', 'An unknown error occurred'));
              },
              () => setFormHasBeenSubmitted(true),
            )}
          >
            <Grid container spacing={2} sx={{ textAlign: 'center' }}>
              <Grid item xs={12}>
                <Typography variant="h4">Reset Password</Typography>
              </Grid>
              <Grid item xs={12}>
                <InputField
                  control={control}
                  label="New Password"
                  name="password"
                  rules={{
                    required: 'Required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters long',
                    },
                  }}
                  sx={{ width: '100%' }}
                  type="password"
                />
              </Grid>
              <Grid item xs={12}>
                <InputField
                  control={control}
                  label="Confirm New Password"
                  name="passwordConfirm"
                  rules={{ validate: value => (value !== password ? 'Passwords do not match' : undefined) }}
                  sx={{ width: '100%' }}
                  type="password"
                />
              </Grid>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'right' }}>
                <Button type="submit" variant="contained" disabled={isSubmitting}>
                  {isSubmitting ? <CircularProgress size={24} /> : 'Reset Password'}
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </Background>
  );
}
